import { createContext, useContext, useEffect, useState } from "react";
import en from "@/i18n/en.json";
import de from "@/i18n/de.json";
import es from "@/i18n/es.json";
import { withProjectUpdates } from "@/i18n/projectUpdates";
import { LEGAL_UI_TRANSLATIONS } from "@/i18n/legalUpdates";
import { PHILOSOPHY_TRANSLATIONS } from "@/i18n/philosophyUpdates";
import { BROCHURE_TRANSLATIONS } from "@/i18n/brochureUpdates";
import { SPONSOR_TRANSLATIONS } from "@/i18n/sponsorUpdates";
import { CAPABILITY_TRANSLATIONS } from "@/i18n/capabilityUpdates";
import { useLocation } from "react-router-dom";

export const LANGUAGES = [
  { code: "it", name: "Italiano", locale: "it-IT" },
  { code: "en", name: "English", locale: "en-GB" },
  { code: "de", name: "Deutsch", locale: "de-DE" },
  { code: "es", name: "Español", locale: "es-ES" },
];
const dictionaries = Object.fromEntries(Object.entries({ en, de, es }).map(([language, catalog]) => [language, { ...withProjectUpdates(catalog, language), ...LEGAL_UI_TRANSLATIONS[language], ...PHILOSOPHY_TRANSLATIONS[language], ...BROCHURE_TRANSLATIONS[language], ...SPONSOR_TRANSLATIONS[language], ...CAPABILITY_TRANSLATIONS[language] }]));
const saved = localStorage.getItem("gfc_language");
let currentLanguage = LANGUAGES.some((l) => l.code === saved) ? saved : "it";
const LanguageContext = createContext(null);
export const getLanguage = () => currentLanguage;
export const getLocale = () => LANGUAGES.find((l) => l.code === currentLanguage).locale;
const normalize = (value) => value.replace(/\s+/g, " ").trim();
const templates = Object.fromEntries(Object.entries(dictionaries).map(([language, entries]) => [language,
  Object.entries(entries).filter(([source]) => /\{\d+\}/.test(source)).map(([source, translated]) => {
    const parts = source.split(/(\{\d+\})/);
    const keys = [];
    const pattern = parts.map((part) => {
      if (/^\{\d+\}$/.test(part)) { keys.push(part); return "(.+?)"; }
      return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }).join("");
    return { regex: new RegExp(`^${pattern}$`), keys, translated };
  })]));

const translateSlot = (value) => t(value);
export function t(value) {
  if (typeof value !== "string" || currentLanguage === "it" || !value.trim()) return value;
  const source = normalize(value);
  let translated = dictionaries[currentLanguage]?.[source];
  if (!translated) {
    for (const template of templates[currentLanguage] || []) {
      const match = source.match(template.regex);
      if (match) {
        translated = template.translated;
        template.keys.forEach((key, i) => { translated = translated.split(key).join(translateSlot(match[i + 1])); });
        break;
      }
    }
  }
  if (!translated && value.startsWith("© ")) {
    return value.replace(/C\.F\. e P\.IVA/, t("C.F. e P.IVA"));
  }
  if (!translated && /[·•]/.test(value)) return value.split(/([·•])/).map((part) => /[·•]/.test(part) ? part : t(part)).join("");
  if (!translated && value.includes("\n")) return value.split("\n").map(t).join("\n");
  if (!translated) return value;
  return `${value.match(/^\s*/)[0]}${translated}${value.match(/\s*$/)[0]}`;
}

export function LanguageProvider({ children }) {
  const { pathname } = useLocation();
  const [language, updateLanguage] = useState(currentLanguage);
  const setLanguage = (code) => {
    if (!LANGUAGES.some((l) => l.code === code)) return;
    currentLanguage = code;
    localStorage.setItem("gfc_language", code);
    updateLanguage(code);
  };
  useEffect(() => {
    document.documentElement.lang = language;
    const legalTitle = { "/privacy": "Informativa Privacy", "/cookie": "Informativa Cookie" }[pathname];
    document.title = legalTitle ? `${t(legalTitle)} — GFC Appalti Generali S.r.l.` : t("GFC Appalti Generali S.r.l. — Costruzioni, Progettazione e Ingegneria");
  }, [language, pathname]);
  useEffect(() => {
    const invalid = (event) => {
      const field = event.target;
      if (!field?.setCustomValidity) return;
      field.setCustomValidity("");
      const state = field.validity;
      const message = state.valueMissing ? (field.type === "checkbox" ? "Conferma il consenso per continuare." : "Campo obbligatorio.") : state.typeMismatch && field.type === "email" ? "Inserisci un indirizzo email valido." : state.tooShort ? "Il testo inserito è troppo breve." : "Il valore inserito non è valido.";
      field.setCustomValidity(t(message));
    };
    const clear = (event) => event.target?.setCustomValidity?.("");
    document.querySelectorAll("input, textarea, select").forEach((field) => field.setCustomValidity(""));
    document.addEventListener("invalid", invalid, true);
    document.addEventListener("input", clear, true);
    document.addEventListener("change", clear, true);
    return () => {
      document.removeEventListener("invalid", invalid, true);
      document.removeEventListener("input", clear, true);
      document.removeEventListener("change", clear, true);
    };
  }, [language]);
  return <LanguageContext.Provider value={{ language, setLanguage, t, locale: getLocale() }}>{children}</LanguageContext.Provider>;
}
export const useLanguage = () => useContext(LanguageContext);
